/**
 * WLED Device Communication Service
 * Handles communication with WLED devices via HTTP JSON API
 * 
 * WLED devices support:
 * - JSON API at /json for state control
 * - mDNS discovery (_wled._tcp.local)
 * - WiFi AP mode for initial setup
 */

import { Platform } from '@/lib/platform'
import { logger } from '@/lib/logger'
import { httpGet, httpPost } from '@/lib/http'

// Create WLED-specific logger
export const wledLogger = {
  info: (...args: any[]) => logger.info('[WLED]', ...args),
  warn: (...args: any[]) => logger.warn('[WLED]', ...args),
  error: (...args: any[]) => logger.error('[WLED]', ...args),
  debug: (...args: any[]) => logger.debug('[WLED]', ...args),
  success: (...args: any[]) => logger.info('[WLED]', ...args)
}

/**
 * WLED device info from /json/info endpoint
 */
export interface WledDeviceInfo {
  ver: string           // WLED version (e.g., "0.14.0")
  vid: number           // Build ID
  leds: {
    count: number       // Total LED count
    rgbw: boolean       // Has white channel
    cct: boolean        // Color temperature support
    maxpwr: number      // Max power in mA
    maxseg: number      // Max segments
    fps: number         // Current FPS
  }
  name: string          // Device name
  udpport: number       // UDP port
  live: boolean         // Receiving live data
  fxcount: number       // Number of effects
  palcount: number      // Number of palettes
  arch: string          // Architecture (esp8266, esp32)
  core: string          // Core version
  freeheap: number      // Free heap memory
  uptime: number        // Uptime in seconds
  brand: string         // Brand (always "WLED")
  product: string       // Product name
  mac: string           // MAC address (lowercase, no colons)
  ip: string            // IP address
  wifi?: {
    bssid: string       // Connected WiFi BSSID
    signal: number      // Signal strength 0-100
    channel: number     // WiFi channel
  }
}

/**
 * WLED segment state
 */
export interface WledSegment {
  id?: number
  start?: number
  stop?: number
  len?: number
  grp?: number
  spc?: number
  of?: number
  on?: boolean
  bri?: number          // 0-255
  n?: string            // Segment name
  col?: [number[], number[]?, number[]?]  // Up to 3 colors, each [R,G,B] or [R,G,B,W]
  fx?: number           // Effect ID
  sx?: number           // Effect speed 0-255
  ix?: number           // Effect intensity 0-255
  pal?: number          // Palette ID
  sel?: boolean         // Selected
  rev?: boolean         // Reversed
  mi?: boolean          // Mirrored
  cct?: number          // Color temperature 0-255 or 1900-10091K
}

/**
 * WLED device state from /json/state endpoint
 */
export interface WledState {
  on: boolean           // On/Off state
  bri: number           // Brightness 0-255
  transition: number    // Transition time in 100ms units
  ps: number            // Current preset (-1 = none)
  pl: number            // Current playlist (-1 = none)
  nl: {
    on: boolean         // Nightlight active
    dur: number         // Duration in minutes
    mode: number        // Nightlight mode
    tbri: number        // Target brightness
    rem: number         // Remaining seconds
  }
  udpn: {
    send: boolean       // UDP sync send
    recv: boolean       // UDP sync receive
  }
  seg: WledSegment[]    // Segments
  mainseg: number       // Main segment index
}

/**
 * WLED full JSON response from /json endpoint
 */
export interface WledJsonResponse {
  state: WledState
  info: WledDeviceInfo
  effects?: string[]
  palettes?: string[]
}

/**
 * Command to set WLED state
 */
export interface WledCommand {
  on?: boolean | 't'    // On/Off or toggle
  bri?: number          // Brightness 0-255
  transition?: number   // Transition time (100ms units)
  seg?: Partial<WledSegment>[]  // Segment commands
  v?: boolean           // Return full state in response
}

/**
 * WiFi configuration for WLED AP provisioning
 */
export interface WledWifiConfig {
  ssid: string
  password: string
  staticIp?: string
  gateway?: string
  subnet?: string
  mdnsName?: string
}

/**
 * WLED hardware LED output (GPIO pin configuration)
 */
export interface WledHardwareOutput {
  start: number         // Start LED index
  len: number           // Number of LEDs
  pin: number[]         // GPIO pins (usually [pin] for single-wire)
  type: number          // LED type code
  order: number         // Color order code (0=GRB, 1=RGB, 2=BRG, etc.)
  skip: number          // LEDs to skip
  ref: boolean          // Refresh required
  rgbw: boolean         // Has white channel
}

/**
 * WLED hardware configuration from /json/cfg
 */
export interface WledHardwareConfig {
  hw: {
    led: {
      total: number
      maxpwr: number
      ins: WledHardwareOutput[]
    }
  }
  id: {
    name: string
    mdns: string
  }
}

/**
 * LED type code to human-readable name mapping
 */
export const LED_TYPE_NAMES: Record<number, string> = {
  22: 'WS281x',
  30: 'SK6812 RGBW',
  31: 'TM1814',
  24: 'WS2801',
  25: 'APA102',
  26: 'LPD8806',
  27: 'P9813',
  28: 'TM1829',
  29: 'UCS8903'
}

/**
 * Color order code to human-readable name mapping
 */
export const COLOR_ORDER_NAMES: Record<number, string> = {
  0: 'GRB',
  1: 'RGB',
  2: 'BRG',
  3: 'RBG',
  4: 'BGR',
  5: 'GBR'
}

/**
 * WLED Device Communication Service
 */
class WledDeviceServiceClass {
  private static readonly DEFAULT_TIMEOUT = 5000
  private static readonly SCAN_TIMEOUT = 1000  // Shorter timeout for network scanning

  /**
   * Quick probe to check if an IP has a WLED device (for network scanning)
   */
  async quickProbe(ipAddress: string): Promise<WledDeviceInfo | null> {
    try {
      const response = await httpGet<WledDeviceInfo>(
        `http://${ipAddress}/json/info`,
        { timeout: WledDeviceServiceClass.SCAN_TIMEOUT }
      )

      if (!response.ok) {
        return null
      }

      // Verify it's actually a WLED device
      if (response.data?.brand === 'WLED' || response.data?.ver) {
        return response.data
      }
      return null
    } catch {
      return null
    }
  }

  /**
   * Get WLED device info
   */
  async getDeviceInfo(ipAddress: string): Promise<WledDeviceInfo | null> {
    try {
      const response = await httpGet<WledDeviceInfo>(
        `http://${ipAddress}/json/info`,
        { timeout: WledDeviceServiceClass.DEFAULT_TIMEOUT }
      )

      if (!response.ok) {
        wledLogger.warn(`Failed to get device info from ${ipAddress}: ${response.status}`)
        return null
      }

      return response.data
    } catch (error) {
      wledLogger.error(`Error getting device info from ${ipAddress}:`, error)
      return null
    }
  }

  /**
   * Get WLED device state
   */
  async getState(ipAddress: string): Promise<WledState | null> {
    try {
      const response = await httpGet<WledState>(
        `http://${ipAddress}/json/state`,
        { timeout: WledDeviceServiceClass.DEFAULT_TIMEOUT }
      )

      if (!response.ok) {
        wledLogger.warn(`Failed to get state from ${ipAddress}: ${response.status}`)
        return null
      }

      return response.data
    } catch (error) {
      wledLogger.error(`Error getting state from ${ipAddress}:`, error)
      return null
    }
  }

  /**
   * Get full WLED JSON (state + info + effects + palettes)
   */
  async getFullJson(ipAddress: string): Promise<WledJsonResponse | null> {
    try {
      const response = await httpGet<WledJsonResponse>(
        `http://${ipAddress}/json`,
        { timeout: WledDeviceServiceClass.DEFAULT_TIMEOUT }
      )

      if (!response.ok) {
        wledLogger.warn(`Failed to get full JSON from ${ipAddress}: ${response.status}`)
        return null
      }

      return response.data
    } catch (error) {
      wledLogger.error(`Error getting full JSON from ${ipAddress}:`, error)
      return null
    }
  }

  /**
   * Set WLED device state
   */
  async setState(ipAddress: string, command: WledCommand): Promise<boolean> {
    try {
      wledLogger.debug(`Setting state on ${ipAddress}:`, command)

      const response = await httpPost<WledState>(
        `http://${ipAddress}/json/state`,
        command as Record<string, unknown>,
        { timeout: WledDeviceServiceClass.DEFAULT_TIMEOUT }
      )

      if (!response.ok) {
        wledLogger.warn(`Failed to set state on ${ipAddress}: ${response.status}`)
        return false
      }

      wledLogger.debug(`State set successfully on ${ipAddress}`)
      return true
    } catch (error) {
      wledLogger.error(`Error setting state on ${ipAddress}:`, error)
      return false
    }
  }

  /**
   * Set the entire strip to a single color
   * This is a convenience method that treats the strip as one light
   */
  async setSolidColor(
    ipAddress: string,
    options: {
      on: boolean
      brightness?: number   // 0-255
      color?: [number, number, number] | [number, number, number, number]  // RGB or RGBW
      colorTemperature?: number  // Kelvin (2700-6500 typical)
      transitionTime?: number  // milliseconds
    }
  ): Promise<boolean> {
    const command: WledCommand = {
      on: options.on,
      v: false
    }

    if (options.brightness !== undefined) {
      command.bri = Math.max(0, Math.min(255, options.brightness))
    }

    if (options.transitionTime !== undefined) {
      // Convert ms to WLED units (100ms)
      command.transition = Math.round(options.transitionTime / 100)
    }

    // Set segment 0 to solid color (effect 0)
    if (options.color || options.colorTemperature !== undefined) {
      const segCommand: Partial<WledSegment> = {
        id: 0,
        fx: 0, // Solid effect
        sel: true
      }

      if (options.color) {
        // Support both RGB [R,G,B] and RGBW [R,G,B,W] colors
        segCommand.col = [options.color as number[]]
      }

      // CCT support - convert Kelvin to WLED's 0-255 range
      // WLED uses 0 = warm (2700K), 255 = cold (6500K)
      if (options.colorTemperature !== undefined) {
        // Map Kelvin range to 0-255
        const minK = 2700
        const maxK = 6500
        const clampedK = Math.max(minK, Math.min(maxK, options.colorTemperature))
        const cct = Math.round(((clampedK - minK) / (maxK - minK)) * 255)
        segCommand.cct = cct
      }

      command.seg = [segCommand]
    }

    return this.setState(ipAddress, command)
  }

  /**
   * Turn device on/off
   */
  async setPower(ipAddress: string, on: boolean): Promise<boolean> {
    return this.setState(ipAddress, { on })
  }

  /**
   * Set brightness
   */
  async setBrightness(ipAddress: string, brightness: number): Promise<boolean> {
    return this.setState(ipAddress, {
      bri: Math.max(0, Math.min(255, brightness))
    })
  }

  /**
   * Check if a device at the given IP is a WLED device
   */
  async isWledDevice(ipAddress: string): Promise<boolean> {
    const info = await this.getDeviceInfo(ipAddress)
    return info !== null && info.brand === 'WLED'
  }

  /**
   * Configure WiFi on a WLED device (for AP provisioning)
   * This is used when the device is in AP mode
   */
  async configureWifi(
    apIpAddress: string,
    config: WledWifiConfig
  ): Promise<boolean> {
    try {
      wledLogger.info(`Configuring WiFi on ${apIpAddress} to connect to ${config.ssid}`)

      // WLED uses form-encoded POST to /settings/wifi
      const formData = new URLSearchParams()
      formData.append('CS', config.ssid)
      formData.append('CP', config.password)
      
      if (config.staticIp) {
        formData.append('I0', config.staticIp.split('.')[0])
        formData.append('I1', config.staticIp.split('.')[1])
        formData.append('I2', config.staticIp.split('.')[2])
        formData.append('I3', config.staticIp.split('.')[3])
      }

      if (config.gateway) {
        formData.append('G0', config.gateway.split('.')[0])
        formData.append('G1', config.gateway.split('.')[1])
        formData.append('G2', config.gateway.split('.')[2])
        formData.append('G3', config.gateway.split('.')[3])
      }

      if (config.subnet) {
        formData.append('S0', config.subnet.split('.')[0])
        formData.append('S1', config.subnet.split('.')[1])
        formData.append('S2', config.subnet.split('.')[2])
        formData.append('S3', config.subnet.split('.')[3])
      }

      if (config.mdnsName) {
        formData.append('CM', config.mdnsName)
      }

      const response = await httpPost(
        `http://${apIpAddress}/settings/wifi`,
        formData.toString(),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          timeout: 10000
        }
      )

      if (response.ok) {
        wledLogger.success(`WiFi configured on ${apIpAddress}`)
        return true
      }

      wledLogger.warn(`WiFi configuration failed: ${response.status}`)
      return false
    } catch (error) {
      wledLogger.error(`Error configuring WiFi on ${apIpAddress}:`, error)
      return false
    }
  }

  /**
   * Reboot a WLED device
   */
  async reboot(ipAddress: string): Promise<boolean> {
    try {
      const response = await httpPost(
        `http://${ipAddress}/json/state`,
        { rb: true },
        { timeout: WledDeviceServiceClass.DEFAULT_TIMEOUT }
      )
      return response.ok
    } catch (error) {
      wledLogger.error(`Error rebooting device at ${ipAddress}:`, error)
      return false
    }
  }

  /**
   * Get hardware configuration from WLED device
   * This includes LED output (GPIO) configuration
   */
  async getHardwareConfig(ipAddress: string): Promise<WledHardwareConfig | null> {
    try {
      const response = await httpGet<WledHardwareConfig>(
        `http://${ipAddress}/json/cfg`,
        { timeout: WledDeviceServiceClass.DEFAULT_TIMEOUT }
      )

      if (!response.ok) {
        wledLogger.warn(`Failed to get hardware config from ${ipAddress}: ${response.status}`)
        return null
      }

      return response.data
    } catch (error) {
      wledLogger.error(`Error getting hardware config from ${ipAddress}:`, error)
      return null
    }
  }

  /**
   * Set the device name on the WLED device
   */
  async setDeviceName(ipAddress: string, name: string): Promise<boolean> {
    try {
      wledLogger.info(`Setting device name on ${ipAddress} to "${name}"`)

      const response = await httpPost(
        `http://${ipAddress}/json/cfg`,
        { id: { name } },
        { timeout: WledDeviceServiceClass.DEFAULT_TIMEOUT }
      )

      if (!response.ok) {
        wledLogger.warn(`Failed to set device name on ${ipAddress}: ${response.status}`)
        return false
      }

      wledLogger.success(`Device name set to "${name}" on ${ipAddress}`)
      return true
    } catch (error) {
      wledLogger.error(`Error setting device name on ${ipAddress}:`, error)
      return false
    }
  }

  /**
   * Set state for a specific segment
   */
  async setSegmentState(
    ipAddress: string,
    segmentId: number,
    options: {
      on?: boolean
      brightness?: number   // 0-255
      color?: [number, number, number] | [number, number, number, number]  // RGB or RGBW
      colorTemperature?: number  // Kelvin
      transitionTime?: number  // milliseconds
    }
  ): Promise<boolean> {
    const segCommand: Partial<WledSegment> = {
      id: segmentId
    }

    // Only set fx: 0 (Solid) if we're explicitly setting a color
    // This preserves effects when just changing brightness or on/off
    if (options.color !== undefined) {
      segCommand.fx = 0 // Solid effect - only when color is explicitly set
    }

    if (options.on !== undefined) {
      segCommand.on = options.on
    }

    if (options.brightness !== undefined) {
      segCommand.bri = Math.max(0, Math.min(255, options.brightness))
    }

    if (options.color) {
      segCommand.col = [options.color as number[]]
    }

    if (options.colorTemperature !== undefined) {
      // Map Kelvin range to 0-255
      const minK = 2700
      const maxK = 6500
      const clampedK = Math.max(minK, Math.min(maxK, options.colorTemperature))
      const cct = Math.round(((clampedK - minK) / (maxK - minK)) * 255)
      segCommand.cct = cct
    }

    const command: WledCommand = {
      seg: [segCommand]
    }

    // When turning a segment on, ensure the master power is also on
    // Otherwise the segment won't be visible even if seg.on = true
    if (options.on === true) {
      command.on = true
    }

    if (options.transitionTime !== undefined) {
      command.transition = Math.round(options.transitionTime / 100)
    }

    wledLogger.debug(`setSegmentState: segment=${segmentId}, command=`, JSON.stringify(command))
    return this.setState(ipAddress, command)
  }

  /**
   * Get LED type name from type code
   */
  getLedTypeName(typeCode: number): string {
    return LED_TYPE_NAMES[typeCode] || `Unknown (${typeCode})`
  }

  /**
   * Get color order name from code
   */
  getColorOrderName(orderCode: number): string {
    return COLOR_ORDER_NAMES[orderCode] || `Unknown (${orderCode})`
  }

  /**
   * Get the list of available effects from a WLED device
   */
  async getEffects(ipAddress: string): Promise<string[] | null> {
    try {
      const url = `http://${ipAddress}/json/effects`
      const response = await httpGet<string[]>(url, { timeout: 5000 })
      
      if (Array.isArray(response)) {
        wledLogger.info(`Got ${response.length} effects from ${ipAddress}`)
        return response
      }
      return null
    } catch (err) {
      wledLogger.error(`Failed to get effects from ${ipAddress}:`, err)
      return null
    }
  }

  /**
   * Get the list of available palettes from a WLED device
   */
  async getPalettes(ipAddress: string): Promise<string[] | null> {
    try {
      const url = `http://${ipAddress}/json/palettes`
      const response = await httpGet<string[]>(url, { timeout: 5000 })
      
      if (Array.isArray(response)) {
        wledLogger.info(`Got ${response.length} palettes from ${ipAddress}`)
        return response
      }
      return null
    } catch (err) {
      wledLogger.error(`Failed to get palettes from ${ipAddress}:`, err)
      return null
    }
  }

  /**
   * Set effect on a specific segment
   * @param ipAddress Device IP
   * @param segmentId Segment ID
   * @param options Effect options
   */
  async setSegmentEffect(
    ipAddress: string,
    segmentId: number,
    options: {
      effectId: number          // Effect ID (fx)
      speed?: number            // Speed 0-255 (sx)
      intensity?: number        // Intensity 0-255 (ix)
      paletteId?: number        // Palette ID (pal)
      colors?: number[][]       // Up to 3 colors [[r,g,b], [r,g,b], [r,g,b]]
      transitionTime?: number   // Transition in ms
    }
  ): Promise<boolean> {
    const segCommand: Record<string, unknown> = {
      id: segmentId,
      fx: options.effectId
    }

    if (options.speed !== undefined) {
      segCommand.sx = Math.max(0, Math.min(255, options.speed))
    }

    if (options.intensity !== undefined) {
      segCommand.ix = Math.max(0, Math.min(255, options.intensity))
    }

    if (options.paletteId !== undefined) {
      segCommand.pal = options.paletteId
    }

    if (options.colors && options.colors.length > 0) {
      // WLED accepts up to 3 colors for effects
      segCommand.col = options.colors.slice(0, 3)
    }

    const command: WledCommand = {
      seg: [segCommand]
    }

    if (options.transitionTime !== undefined) {
      command.transition = Math.round(options.transitionTime / 100)
    }

    wledLogger.info(`Setting effect on ${ipAddress} segment ${segmentId}: fx=${options.effectId}`)
    wledLogger.debug(`Effect command:`, JSON.stringify(command))
    
    return this.setState(ipAddress, command)
  }
}

// Export singleton instance
export const wledDeviceService = new WledDeviceServiceClass()
